/**
 * Theme Application Utility
 * Applies theme configurations from database to CSS variables
 */

import { clearFontSize } from '@/utils/font-size-application.util'

/** Event-based effects: subtle, culturally relevant, age-appropriate (e.g. Diwali, Republic Day, Holi). */
export interface ThemeEventEffectsConfig {
  diyaGlow?: { enabled: boolean; intensity?: 'low' | 'medium' }
  tricolorWave?: { enabled: boolean; intensity?: 'low' | 'medium' }
  holiColors?: { enabled: boolean; intensity?: 'low' | 'medium' }
  petalFall?: { enabled: boolean; intensity?: 'low' | 'medium' }
  spotlight?: { enabled: boolean; intensity?: 'low' | 'medium' }
}

export interface ThemeEffectsConfig {
  global?: {
    snowfall?: { enabled: boolean; intensity?: 'low' | 'medium' | 'high' }
    sparkles?: { enabled: boolean; color?: string }
    floatingLights?: { enabled: boolean; intensity?: string }
    confetti?: { enabled: boolean; colors?: string[] }
    fireworks?: { enabled: boolean; intensity?: 'low' | 'medium' | 'high' }
  }
  eventEffects?: ThemeEventEffectsConfig
  components?: {
    button?: {
      hover?: {
        glow?: { enabled: boolean; color?: string }
        sparkles?: { enabled: boolean }
        ripple?: { enabled: boolean }
      }
    }
    card?: {
      hover?: {
        glow?: { enabled: boolean; color?: string }
      }
    }
  }
}

export interface ThemeBackgroundsConfig {
  navbar?: {
    light?: string
    dark?: string
  }
  body?: {
    light?: string
    dark?: string
  }
}

/** Intent colors: semantic meaning for action icons, badges, status. Theme-controlled. */
export interface ThemeIntentConfig {
  success?: string
  'success-foreground'?: string
  info?: string
  'info-foreground'?: string
  warning?: string
  'warning-foreground'?: string
  neutral?: string
  'neutral-foreground'?: string
  processing?: string
  'processing-foreground'?: string
  [key: string]: string | undefined
}

export interface DatabaseTheme {
  id: string
  key: string
  name: string
  description: string | null
  mode: 'light' | 'dark' | 'system'
  config: {
    light?: Record<string, any> & { intent?: ThemeIntentConfig }
    dark?: Record<string, any> & { intent?: ThemeIntentConfig }
    effects?: ThemeEffectsConfig
    backgrounds?: ThemeBackgroundsConfig
    [key: string]: any
  }
  isDefault: boolean
  isActive: boolean
  isSystem: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Figma default theme tokens (mirrors Vidhyantra_Backend/seed/themes/themes.seed.ts).
 * Used when no DB theme is loaded so buttons, toggles, and accents stay consistent.
 */
/** Legacy Figma brand pink was incorrectly used as destructive; use standard error red. */
const LEGACY_DESTRUCTIVE_HSL = '340 82% 52%'
const STANDARD_ERROR_DESTRUCTIVE_HSL = '0 72% 51%'

function normalizeDestructiveToken(root: HTMLElement): void {
  const destructive = root.style.getPropertyValue('--destructive').trim()
  if (!destructive || destructive === LEGACY_DESTRUCTIVE_HSL) {
    root.style.setProperty('--destructive', STANDARD_ERROR_DESTRUCTIVE_HSL)
  }
}

function syncBorderInteractiveFromTheme(root: HTMLElement): void {
  const styles = getComputedStyle(root)
  const ring = styles.getPropertyValue('--ring').trim()
  const primary = styles.getPropertyValue('--primary').trim()
  const interactive = ring || primary
  if (interactive) {
    root.style.setProperty('--border-interactive', interactive)
  }
}

const DEFAULT_THEME_TOKENS: Record<'light' | 'dark', Record<string, string>> = {
  light: {
    background: '220 14% 96%',
    foreground: '210 17% 9%',
    card: '0 0% 100%',
    'card-foreground': '210 17% 9%',
    popover: '0 0% 100%',
    'popover-foreground': '210 17% 9%',
    primary: '253 41% 48%',
    'primary-foreground': '0 0% 100%',
    secondary: '220 13% 91%',
    'secondary-foreground': '210 17% 9%',
    muted: '220 13% 91%',
    'muted-foreground': '220 9% 46%',
    accent: '253 80% 64%',
    'accent-foreground': '0 0% 100%',
    destructive: STANDARD_ERROR_DESTRUCTIVE_HSL,
    'destructive-foreground': '0 0% 100%',
    border: '220 13% 91%',
    input: '220 13% 91%',
    ring: '253 41% 48%',
    radius: '0.75rem',
    'side-menu': '253 41% 48%',
    'side-menu-foreground': '0 0% 100%',
    'side-menu-border': '0 0% 100% / 0.15',
    'side-menu-gradient-to': '253 80% 64%',
  },
  dark: {
    background: '0 0% 0%',
    foreground: '0 0% 100%',
    card: '0 0% 9%',
    'card-foreground': '0 0% 100%',
    popover: '0 0% 9%',
    'popover-foreground': '0 0% 100%',
    primary: '0 0% 100%',
    'primary-foreground': '0 0% 0%',
    secondary: '0 0% 12%',
    'secondary-foreground': '0 0% 100%',
    muted: '0 0% 12%',
    'muted-foreground': '0 0% 63%',
    accent: '0 0% 18%',
    'accent-foreground': '0 0% 100%',
    destructive: '0 0% 45%',
    'destructive-foreground': '0 0% 100%',
    border: '0 0% 16%',
    input: '0 0% 14%',
    ring: '0 0% 63%',
    radius: '0.75rem',
    'side-menu': '0 0% 7%',
    'side-menu-foreground': '0 0% 100%',
    'side-menu-border': '0 0% 100% / 0.1',
    'side-menu-gradient-to': '0 0% 12%',
  },
}

export function applyDefaultThemeTokens(mode: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const tokens = DEFAULT_THEME_TOKENS[mode]
  Object.entries(tokens).forEach(([key, value]) => {
    root.style.setProperty(`--${key}`, value)
  })
  normalizeDestructiveToken(root)
  syncBorderInteractiveFromTheme(root)
  root.setAttribute('data-theme-key', 'default-fallback')
  root.removeAttribute('data-theme-id')
  applyShellDesignTokens(mode)
}

/**
 * Apply theme configuration to CSS variables
 * @param theme - Theme from database with config.light and config.dark
 * @param mode - Current mode ('light' | 'dark')
 */
export function applyThemeConfig(theme: DatabaseTheme | null, mode: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement

  if (!theme || !theme.config) {
    // If no theme, clear any custom CSS variables
    clearThemeVariables()
    return
  }

  // Get the config for the current mode
  const config = mode === 'light' ? theme.config.light || {} : theme.config.dark || {}

  // Apply flat CSS variables from theme config (background, foreground, primary, etc.)
  Object.entries(config).forEach(([key, value]) => {
    if (key === 'intent') return // handled below
    if (typeof value === 'string' && value.trim() !== '') {
      const cssVarName = key
        .replace(/([A-Z])/g, '-$1')
        .toLowerCase()
        .replace(/^-/, '')
      root.style.setProperty(`--${cssVarName}`, value)
    }
  })

  // Apply intent colors from config.light.intent / config.dark.intent (theme is sole source of truth)
  const intent = config.intent
  if (intent && typeof intent === 'object') {
    Object.entries(intent).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim() !== '') {
        const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^-/, '')
        root.style.setProperty(`--intent-${cssKey}`, value)
      }
    })
  }

  normalizeDestructiveToken(root)
  syncBorderInteractiveFromTheme(root)

  // Set data attribute to indicate theme is applied
  root.setAttribute('data-theme-id', theme.id)
  root.setAttribute('data-theme-key', theme.key)

  // Apply theme effects configuration
  applyThemeEffects(theme)

  // Apply theme backgrounds
  applyThemeBackgrounds(theme, mode)

  // Shell grey scale + sidebar tokens derived from active theme
  applyShellDesignTokens(mode)
}

/**
 * Pick a distinct gradient end stop from theme tokens (sidebar rail: side-menu → brand).
 */
function pickSidebarGradientEnd(
  from: string,
  primary: string,
  secondary: string,
  accent: string
): string {
  if (primary && primary !== from) return primary
  if (accent && accent !== from) return accent
  if (secondary && secondary !== from) {
    const match = secondary.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/)
    // Skip grey/muted secondaries — use warm hue shift for visible brand gradient
    if (match && parseFloat(match[2]) >= 25) return secondary
  }
  return shiftHslHue(from, 76)
}

/** Shift HSL hue when accent equals side-menu (default pink → warm orange). */
function shiftHslHue(hsl: string, degrees: number): string {
  const match = hsl.match(/^([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/)
  if (!match) return '253 80% 64%'
  const hue = (parseFloat(match[1]) + degrees) % 360
  return `${Math.round(hue)} ${match[2]}% ${match[3]}%`
}

/**
 * Sync sidebar shell tokens from the active theme (called after every theme apply).
 */
export function syncSidebarShellTokens(): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const styles = getComputedStyle(root)

  const sideMenu =
    styles.getPropertyValue('--side-menu').trim() ||
    styles.getPropertyValue('--primary').trim() ||
    '253 41% 48%'
  const primary = styles.getPropertyValue('--primary').trim() || sideMenu
  const secondary = styles.getPropertyValue('--secondary').trim() || primary
  const accent = styles.getPropertyValue('--accent').trim() || primary
  const sideMenuFg = styles.getPropertyValue('--side-menu-foreground').trim() || '0 0% 100%'
  const sideMenuActive = styles.getPropertyValue('--side-menu-active').trim()
  const gradientFrom =
    styles.getPropertyValue('--side-menu-gradient-from').trim() || sideMenu
  const gradientToExplicit = styles.getPropertyValue('--side-menu-gradient-to').trim()

  root.style.setProperty('--sidebar-gradient-from', gradientFrom)
  root.style.setProperty(
    '--sidebar-gradient-to',
    gradientToExplicit || pickSidebarGradientEnd(sideMenu, primary, secondary, accent)
  )
  root.style.setProperty(
    '--sidebar-active-bg',
    sideMenuActive || `${sideMenuFg.replace(/\s*\/.*$/, '').trim()} / 0.19`
  )
  // Keep shadcn sidebar fallback aligned with theme side-menu
  root.style.setProperty('--sidebar-background', sideMenu)
}

/**
 * Shell grey scale (structural) + topbar chrome. Mode-aware so dark mode
 * gets black page surfaces and light text greys (Cursor Settings–style).
 * Sidebar colors come from syncSidebarShellTokens().
 */
export function applyShellDesignTokens(mode?: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const effectiveMode =
    mode ?? (root.classList.contains('dark') ? 'dark' : 'light')

  if (effectiveMode === 'dark') {
    // Inverted greys: grey-1 ≈ white text, grey-6 ≈ black page
    root.style.setProperty('--grey-1', '0 0% 98%')
    root.style.setProperty('--grey-2', '0 0% 63%')
    root.style.setProperty('--grey-3', '0 0% 45%')
    root.style.setProperty('--grey-4', '0 0% 14%')
    root.style.setProperty('--grey-5', '0 0% 16%')
    root.style.setProperty('--grey-6', '0 0% 0%')
    root.style.setProperty('--shell-topbar-bg', 'hsl(var(--card))')
    root.style.setProperty('--shell-topbar-border', 'hsl(var(--border))')
    root.style.setProperty('--shell-search-bg', 'hsl(var(--grey-4))')
  } else {
    root.style.setProperty('--grey-1', '210 17% 9%')
    root.style.setProperty('--grey-2', '220 9% 46%')
    root.style.setProperty('--grey-3', '218 11% 65%')
    root.style.setProperty('--grey-4', '220 14% 96%')
    root.style.setProperty('--grey-5', '220 13% 91%')
    root.style.setProperty('--grey-6', '210 20% 98%')
    // White navbar + #F4F4F6 search for all authenticated scopes
    root.style.setProperty('--shell-topbar-bg', '#ffffff')
    root.style.setProperty('--shell-topbar-border', 'hsl(var(--border))')
    root.style.setProperty('--shell-search-bg', '#f4f4f6')
  }
  syncSidebarShellTokens()
}

/**
 * Clear all theme CSS variables
 */
/**
 * Public / pre-app shell: light document, no custom theme tokens, default text scale.
 * Used before React paint on public URLs and whenever the user is on a public route.
 */
export function resetDocumentAppearanceToDefaults(): void {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.classList.remove('dark')
  clearThemeVariables()
  clearFontSize()
  for (const c of ['text-sm', 'text-md', 'text-lg', 'text-xl']) {
    root.classList.remove(c)
  }
  root.classList.add('text-md')
  root.setAttribute('data-text-size', 'md')
  applyShellDesignTokens('light')
}

export function clearThemeVariables(): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement

  // List of CSS variables that might be set by themes
  const themeVariables = [
    'background',
    'foreground',
    'card',
    'card-foreground',
    'popover',
    'popover-foreground',
    'primary',
    'primary-foreground',
    'secondary',
    'secondary-foreground',
    'muted',
    'muted-foreground',
    'accent',
    'accent-foreground',
    'destructive',
    'destructive-foreground',
    'border',
    'input',
    'ring',
    'radius',
    'side-menu',
    'side-menu-foreground',
    'side-menu-border',
    'side-menu-gradient-from',
    'side-menu-gradient-to',
    'header',
    'header-foreground',
    'header-border',
    'side-menu-active',
    'side-menu-active-foreground',
    'table-border',
    'intent-success',
    'intent-success-foreground',
    'intent-info',
    'intent-info-foreground',
    'intent-warning',
    'intent-warning-foreground',
    'intent-neutral',
    'intent-neutral-foreground',
    'intent-processing',
    'intent-processing-foreground',
  ]

  themeVariables.forEach((varName) => {
    root.style.removeProperty(`--${varName}`)
  })

  root.removeAttribute('data-theme-id')
  root.removeAttribute('data-theme-key')
  root.removeAttribute('data-theme-effects')
  root.style.removeProperty('--theme-navbar-bg')
  
  if (typeof document !== 'undefined') {
    const body = document.body
    body.style.backgroundImage = ''
    body.style.backgroundSize = ''
    body.style.backgroundPosition = ''
    body.style.backgroundRepeat = ''
    body.style.backgroundAttachment = ''
  }
}

/**
 * Apply theme effects configuration
 * Sets data attribute for effect manager to read
 * @param theme - Theme from database with effects config
 */
export function applyThemeEffects(theme: DatabaseTheme | null): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement

  if (!theme || !theme.config?.effects) {
    // Clear effects if no theme or no effects config
    root.removeAttribute('data-theme-effects')
    return
  }

  // Set effects config as JSON string in data attribute
  // Effect manager component will watch this attribute
  try {
    const effectsJson = JSON.stringify(theme.config.effects)
    root.setAttribute('data-theme-effects', effectsJson)
  } catch (error) {
    root.removeAttribute('data-theme-effects')
  }
}

/**
 * Normalize background value for CSS background-image.
 * - If value is a CSS gradient (linear-gradient, radial-gradient, etc.), use as-is.
 * - Otherwise treat as URL and wrap in url().
 */
function toBackgroundImageValue(value: string): string {
  const v = value.trim()
  if (!v) return ''
  if (/^(linear-gradient|radial-gradient|conic-gradient|repeating-linear-gradient|repeating-radial-gradient)\s*\(/i.test(v)) {
    return v
  }
  return `url(${v})`
}

/**
 * Apply theme background images or gradients
 * Applies background to navbar and body based on theme config.
 * Supports image URLs (wrapped in url()) and CSS gradients (linear-gradient, etc.) for navbar/body.
 * @param theme - Theme from database with backgrounds config
 * @param mode - Current mode ('light' | 'dark')
 */
export function applyThemeBackgrounds(theme: DatabaseTheme | null, mode: 'light' | 'dark'): void {
  if (typeof document === 'undefined') return

  const root = document.documentElement
  const body = document.body

  if (!theme || !theme.config?.backgrounds) {
    // Clear backgrounds if no theme or no backgrounds config
    root.style.removeProperty('--theme-navbar-bg')
    root.style.removeProperty('--theme-body-bg')
    body.style.backgroundImage = ''
    return
  }

  const backgrounds = theme.config.backgrounds

  // Apply navbar background (URL or gradient)
  if (backgrounds.navbar) {
    const navbarBg = mode === 'light' ? backgrounds.navbar.light : backgrounds.navbar.dark
    if (navbarBg) {
      root.style.setProperty('--theme-navbar-bg', toBackgroundImageValue(navbarBg))
    } else {
      root.style.removeProperty('--theme-navbar-bg')
    }
  } else {
    root.style.removeProperty('--theme-navbar-bg')
  }

  // Apply body background (URL or gradient)
  if (backgrounds.body) {
    const bodyBg = mode === 'light' ? backgrounds.body.light : backgrounds.body.dark
    if (bodyBg) {
      body.style.backgroundImage = toBackgroundImageValue(bodyBg)
      const isGradient = /^(linear-gradient|radial-gradient|conic-gradient|repeating-)/i.test(bodyBg.trim())
      if (isGradient) {
        body.style.backgroundSize = ''
        body.style.backgroundPosition = ''
        body.style.backgroundRepeat = ''
        body.style.backgroundAttachment = ''
      } else {
        body.style.backgroundSize = 'cover'
        body.style.backgroundPosition = 'center'
        body.style.backgroundRepeat = 'no-repeat'
        body.style.backgroundAttachment = 'fixed'
      }
    } else {
      body.style.backgroundImage = ''
      body.style.backgroundSize = ''
      body.style.backgroundPosition = ''
      body.style.backgroundRepeat = ''
      body.style.backgroundAttachment = ''
    }
  } else {
    body.style.backgroundImage = ''
    body.style.backgroundSize = ''
    body.style.backgroundPosition = ''
    body.style.backgroundRepeat = ''
    body.style.backgroundAttachment = ''
  }
}

